/**
 * CoPilot conversation runner (SR2 P1.1) — drives a streamed turn into the
 * headless store via a state callback. Transport-agnostic: the caller supplies
 * a `stream` factory (SDK on web, expo/fetch on native) yielding
 * `CoPilotStreamEvent`s. Keeps the store pure — this thin driver owns only the
 * iteration + local-echo bookkeeping.
 */

import type { CoPilotStreamEvent } from '@tonnie/types';

import {
  appendUserMessage,
  initialConversationState,
  reduceConversation,
  type CoPilotConversationState,
} from './conversation-store';

export type CoPilotStreamFactory = (message: string) => AsyncIterable<CoPilotStreamEvent>;

export interface RunTurnOptions {
  /** Local id for the user's echoed message. */
  userMessageId: string;
  /**
   * Called on every state change so the view can re-render. It must make the
   * new state what `current()` returns next — the runner folds each event into it.
   */
  onState: (state: CoPilotConversationState) => void;
}

/**
 * Run one turn: echo the user's message, then fold every stream event into the
 * state. Returns the final state. A thrown transport error surfaces as a
 * synthetic typed `error` event so the view path is identical to a server-sent
 * error (never an unhandled rejection, never a hang).
 *
 * Each event is folded into the conversation as it is NOW (`current()`), never
 * into a copy the turn took when it started. The stream outlives the reply —
 * follow-up chips arrive after `end` — and the conversation changes meanwhile:
 * a confirmed proposal is dismissed and gets its "done" notice, feedback is
 * recorded. Folding into a stale copy published that copy on the next event and
 * undid all of it: the confirmed card came back with its Confirm button, and
 * confirming again failed because the action had already run (cold run 2,
 * 29 Sep, block 17 S04).
 */
export async function runConversationTurn(
  current: () => CoPilotConversationState,
  message: string,
  stream: CoPilotStreamFactory,
  options: RunTurnOptions
): Promise<CoPilotConversationState> {
  let state = appendUserMessage(current(), options.userMessageId, message);
  options.onState(state);

  try {
    for await (const event of stream(message)) {
      state = reduceConversation(current(), event);
      options.onState(state);
    }
  } catch {
    state = reduceConversation(current(), {
      type: 'error',
      code: 'internal',
      messageKey: 'copilot.errors.internal',
      retryable: true,
    });
    options.onState(state);
  }

  return state;
}

export { initialConversationState };
